import { useReducer, useCallback, useMemo, useEffect } from 'react';
import { applyFx, summarize } from './scoring';

const STORAGE_KEY = 'sim-results-v1';
const SESSION_PREFIX = 'sim-state-v2-';

const initialKpis = (pack) => {
  const k = { energy: 80, trust: 60 };
  pack.kpis.forEach((x) => {
    k[x.key] = x.start;
  });
  return k;
};

// The URL owns navigation (intro / step n / result); this state owns answers,
// KPIs and delayed effects. `index` is the step currently shown, `answers`
// holds every submitted step, `visited` is the furthest step reached (pending
// effects fire the first time a step is reached, and their notices are kept
// per step so going back shows them again).
const fresh = (pack) => ({
  v: 2,
  packId: pack.id,
  index: 0,
  kpis: initialKpis(pack),
  log: [],
  answers: {},
  pending: [],
  notices: {},
  visited: -1,
  lastFx: null
});

const load = (pack) => {
  try {
    const s = JSON.parse(sessionStorage.getItem(SESSION_PREFIX + pack.id));
    return s && s.v === 2 && s.packId === pack.id ? s : fresh(pack);
  } catch (e) {
    return fresh(pack);
  }
};

const save = (state) => {
  try {
    sessionStorage.setItem(SESSION_PREFIX + state.packId, JSON.stringify(state));
  } catch (e) {
    /* storage unavailable: progress lives in memory only */
  }
};

function reducer(state, action) {
  switch (action.type) {
    case 'reset':
      return fresh(action.pack);
    case 'submit': {
      const { result } = action;
      const pending = [...state.pending];
      (result.later || []).forEach((l) => {
        pending.push({ at: state.index + l.after, note: l.note, fx: l.fx, stepId: result.stepId });
      });
      return {
        ...state,
        kpis: applyFx(state.kpis, result.fx),
        lastFx: result.fx || null,
        pending,
        log: [...state.log, { stepId: result.stepId, score: result.score, fx: result.fx || null }],
        answers: { ...state.answers, [result.stepId]: result }
      };
    }
    case 'goto': {
      const { index } = action;
      if (index <= state.visited) return { ...state, index, lastFx: null };
      // First visit: fire delayed effects scheduled for this step.
      const fired = state.pending.filter((p) => p.at <= index);
      let kpis = state.kpis;
      fired.forEach((p) => {
        kpis = applyFx(kpis, p.fx);
      });
      return {
        ...state,
        index,
        kpis,
        lastFx: null,
        pending: state.pending.filter((p) => p.at > index),
        notices: fired.length ? { ...state.notices, [index]: fired } : state.notices,
        visited: index
      };
    }
    default:
      return state;
  }
}

export const readBest = (packId) => {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return all[packId] || null;
  } catch (e) {
    return null;
  }
};

const writeBest = (packId, score100) => {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    if (!all[packId] || all[packId].score < score100)
      all[packId] = { score: score100, at: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (e) {
    /* storage unavailable: ignore */
  }
};

export default function useSimulation(pack) {
  const [state, dispatch] = useReducer(reducer, pack, load);
  const total = pack.steps.length;
  const step = pack.steps[state.index];
  const answeredCount = state.log.length;
  const finished = answeredCount >= total;

  useEffect(() => {
    save(state);
  }, [state]);

  const reset = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_PREFIX + pack.id);
    } catch (e) {
      /* ignore */
    }
    dispatch({ type: 'reset', pack });
  }, [pack]);
  const goto = useCallback((index) => dispatch({ type: 'goto', index }), []);
  const submit = useCallback(
    (result) => dispatch({ type: 'submit', result: { ...result, stepId: step.id } }),
    [step]
  );

  const summary = useMemo(() => {
    if (!finished) return null;
    const s = summarize(pack, state.log);
    writeBest(pack.id, s.score100);
    return s;
  }, [finished, state.log, pack]);

  return {
    state,
    step,
    total,
    answeredCount,
    finished,
    answered: step ? state.answers[step.id] || null : null,
    notices: state.notices[state.index] || [],
    reset,
    goto,
    submit,
    summary
  };
}
